import { get, post } from '../api.js';
import { wrapForGrant, wrapForLinkSecret, wrapRawKeyForGrant, wrapRawKeyForLinkSecret, unwrapFileKey, decryptItem, rewrapGrantForRecipient, unwrapGrantFileKey, base64urlEncode, encryptClaimSecret, sha256hex, getRawItemKey, prepareLinkGrant, unwrapReceivedGrantKey, wipe } from '../crypto.js';
import { navigate, currentParams } from '../router.js';
import { setTopbar, lucideIcons, esc } from '../app.js';
import { decryptGroupName } from '../item-decrypt.js';

export async function renderGrantCreate(container) {
  setTopbar('Create Grant', '');

  const { params } = currentParams();
  const preselectedItemId = params.get('item_id') || '';
  const preselectedGroupId = params.get('group_id') || '';
  const fromGrantId = params.get('from_grant') || '';

  // Load items and groups for the select dropdowns (only for owner flow)
  let items = [];
  let groups = [];
  if (!fromGrantId) {
    try { items = await get('/items'); } catch (_) {}
    try { groups = await get('/groups'); } catch (_) {}
  }

  const groupsDecrypted = groups.map(g => ({ id: g.id, name: decryptGroupName(g) }));
  const defaultMode = preselectedGroupId ? 'group' : 'item';

  container.innerHTML = `
    <div class="breadcrumb" id="back-to-grants"><i data-lucide="arrow-left" style="width:16px;height:16px"></i> Back to Grants</div>
    <div class="card" style="max-width:560px">
      <div id="grant-msg"></div>
      <form id="grant-form">
        ${fromGrantId ? `
          <div class="form-group">
            <label>Item</label>
            <input class="form-input" type="text" value="Re-sharing from grant ${esc(fromGrantId.slice(0, 8))}..." readonly>
            <input type="hidden" name="item_id" value="${esc(preselectedItemId)}">
          </div>
        ` : `
          <div class="form-group">
            <label>Grant Type</label>
            <div style="display:flex;gap:8px;padding:4px 0">
              <button type="button" class="btn btn-sm ${defaultMode === 'item' ? 'btn-primary' : 'btn-secondary'}" id="mode-item">Single Item</button>
              <button type="button" class="btn btn-sm ${defaultMode === 'group' ? 'btn-primary' : 'btn-secondary'}" id="mode-group">Group</button>
            </div>
          </div>
          <div class="form-group" id="item-select-group" style="${defaultMode === 'group' ? 'display:none' : ''}">
            <label>Item</label>
            <select class="form-input" name="item_id">
              <option value="">Select an item...</option>
              ${items.map(i => `<option value="${i.id}" ${i.id === preselectedItemId ? 'selected' : ''}>${esc(i.id.slice(0, 8))} (${esc(i.item_type)})</option>`).join('')}
            </select>
          </div>
          <div class="form-group" id="group-select-group" style="${defaultMode === 'item' ? 'display:none' : ''}">
            <label>Group</label>
            <select class="form-input" name="group_id">
              <option value="">Select a group...</option>
              ${groupsDecrypted.map(g => `<option value="${g.id}" ${g.id === preselectedGroupId ? 'selected' : ''}>${esc(g.name)}</option>`).join('')}
            </select>
          </div>
        `}
        <div class="form-group">
          <label>Recipient Email</label>
          <input class="form-input" type="email" name="grantee_email" placeholder="bob@example.com" required>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
          <div class="form-group"><label>Max Views</label><input class="form-input" type="number" name="max_views" placeholder="Unlimited" min="1"></div>
          <div class="form-group"><label>Expires</label><input class="form-input" type="datetime-local" name="expires_at"></div>
        </div>
        <div class="form-group">
          <label>TTL after first access</label>
          <div style="display:grid;grid-template-columns:1fr auto;gap:8px;align-items:center">
            <input class="form-input" type="number" name="ttl_value" placeholder="No limit" min="1">
            <select class="form-input" name="ttl_unit" style="width:auto">
              <option value="3600">hours</option>
              <option value="86400">days</option>
              <option value="60">minutes</option>
            </select>
          </div>
        </div>
        <div class="form-group">
          <label>Allowed Operations</label>
          <div style="display:flex;gap:16px;padding:8px 0">
            <label style="display:flex;align-items:center;gap:6px;font-weight:normal;cursor:pointer"><input type="checkbox" name="op_view" checked> View</label>
            <label style="display:flex;align-items:center;gap:6px;font-weight:normal;cursor:pointer"><input type="checkbox" name="op_download" checked> Download</label>
            <label style="display:flex;align-items:center;gap:6px;font-weight:normal;cursor:pointer"><input type="checkbox" name="op_reshare"> Re-share</label>
          </div>
        </div>
        <div class="form-group">
          <label>IP Allowlist</label>
          <input class="form-input" type="text" name="ip_allowlist" placeholder="e.g. 203.0.113.0/24 (optional)">
        </div>
        <div style="display:flex;align-items:center;justify-content:space-between;padding:12px 0;border-top:1px solid var(--border);margin-top:8px">
          <div><div style="font-weight:500;font-size:14px">Notify on access</div><div style="font-size:12px;color:var(--text-secondary)">Send email notification when accessed</div></div>
          <div class="toggle on" id="notify-toggle"><div class="knob"></div></div>
        </div>
        <div style="margin-top:20px"><button type="submit" class="btn btn-primary">Create Grant</button></div>
      </form>
    </div>
  `;
  lucideIcons();

  document.getElementById('back-to-grants').onclick = () => navigate('#/grants');

  // Mode toggle
  let grantMode = defaultMode;
  const modeItemBtn = document.getElementById('mode-item');
  const modeGroupBtn = document.getElementById('mode-group');
  const itemSelectGroup = document.getElementById('item-select-group');
  const groupSelectGroup = document.getElementById('group-select-group');

  if (modeItemBtn && modeGroupBtn) {
    modeItemBtn.onclick = () => {
      grantMode = 'item';
      modeItemBtn.className = 'btn btn-sm btn-primary';
      modeGroupBtn.className = 'btn btn-sm btn-secondary';
      itemSelectGroup.style.display = '';
      groupSelectGroup.style.display = 'none';
    };
    modeGroupBtn.onclick = () => {
      grantMode = 'group';
      modeGroupBtn.className = 'btn btn-sm btn-primary';
      modeItemBtn.className = 'btn btn-sm btn-secondary';
      groupSelectGroup.style.display = '';
      itemSelectGroup.style.display = 'none';
    };
  }

  // Toggle
  document.getElementById('notify-toggle').onclick = function() {
    this.classList.toggle('on');
  };

  document.getElementById('grant-form').onsubmit = async (e) => {
    e.preventDefault();
    const msg = document.getElementById('grant-msg');
    const btn = e.target.querySelector('button[type=submit]');
    btn.disabled = true;
    msg.innerHTML = '';

    // Show encrypt overlay
    const overlay = document.createElement('div');
    overlay.className = 'encrypt-overlay';
    overlay.innerHTML = `<div class="encrypt-box"><div class="spinner"></div><p>Wrapping keys...</p><div class="mono" id="wrap-progress">X25519 key exchange</div></div>`;
    document.body.appendChild(overlay);

    try {
      const granteeEmail = e.target.grantee_email.value;
      const maxViews = e.target.max_views.value ? parseInt(e.target.max_views.value) : undefined;
      const expiresAt = e.target.expires_at.value ? new Date(e.target.expires_at.value).toISOString() : undefined;
      const notifyOnAccess = document.getElementById('notify-toggle').classList.contains('on');
      const ttlValue = e.target.ttl_value.value ? parseInt(e.target.ttl_value.value) : undefined;
      const ttlUnit = parseInt(e.target.ttl_unit.value);

      const allowedOps = [];
      if (e.target.op_view.checked) allowedOps.push('view');
      if (e.target.op_download.checked) allowedOps.push('download');
      if (e.target.op_reshare.checked) allowedOps.push('re-share');
      if (allowedOps.length === 0) { msg.innerHTML = '<div class="msg msg-error">Select at least one allowed operation</div>'; btn.disabled = false; overlay.remove(); return; }

      const policy = { notify_on_access: notifyOnAccess, allowed_ops: allowedOps };
      if (maxViews) policy.max_views = maxViews;
      if (expiresAt) policy.expires_at = expiresAt;
      if (ttlValue) policy.ttl_seconds = ttlValue * ttlUnit;

      const ipAllowlist = e.target.ip_allowlist.value.trim();
      if (ipAllowlist) policy.ip_allowlist = ipAllowlist.split(/[,\s]+/).filter(Boolean);

      let recipientData = null;
      try {
        recipientData = await get(`/users/public-key?email=${encodeURIComponent(granteeEmail)}`);
      } catch (lookupErr) {
        if (!lookupErr.message.includes('404') && !lookupErr.message.toLowerCase().includes('not found')) {
          throw lookupErr;
        }
      }

      const isGroupGrant = !fromGrantId && grantMode === 'group';

      if (fromGrantId) {
        // Re-share flow (single item only for now)
        const itemIdVal = e.target.item_id.value;
        const sourceGrant = await get(`/grants/${fromGrantId}`);

        if (recipientData) {
          const wrapped = rewrapGrantForRecipient(sourceGrant.wrapped_key, sourceGrant.ephemeral_pubkey, recipientData.public_key);
          const grantWrappedKey = wrapped.grant_wrapped_key;

          let fileWrappedKeyForGrant = null;
          if (sourceGrant.file_wrapped_key && sourceGrant.file_wrapped_key.length > 0) {
            const rawFileKey = unwrapGrantFileKey(sourceGrant.file_wrapped_key);
            const fileWrapped = wrapRawKeyForGrant(rawFileKey, recipientData.public_key);
            wipe(rawFileKey);
            fileWrappedKeyForGrant = [...fileWrapped.ephemeral_pubkey, ...fileWrapped.nonce, ...fileWrapped.wrapped_key];
          }

          await post('/grants', {
            item_id: itemIdVal,
            grantee_email: granteeEmail,
            wrapped_key: grantWrappedKey,
            ephemeral_pubkey: wrapped.ephemeral_pubkey,
            policy,
            file_wrapped_key: fileWrappedKeyForGrant,
          });

          overlay.remove();
          showGrantSuccess(null, container);
        } else {
          const itemKey = unwrapReceivedGrantKey(sourceGrant.wrapped_key, sourceGrant.ephemeral_pubkey);
          let rawFileKey = null;
          if (sourceGrant.file_wrapped_key && sourceGrant.file_wrapped_key.length > 0) {
            rawFileKey = unwrapGrantFileKey(sourceGrant.file_wrapped_key);
          }
          const lg = prepareLinkGrant(itemKey, rawFileKey);
          wipe(itemKey, rawFileKey);

          const grant = await post('/grants', {
            item_id: itemIdVal,
            grantee_email: granteeEmail,
            wrapped_key: [...lg.nonce, ...lg.wrapped_key],
            ephemeral_pubkey: [],
            policy,
            file_wrapped_key: lg.file_wrapped_key ? [...lg.file_wrapped_key] : null,
            claim_token_hash: lg.claim_token_hash,
            claim_ciphertext: Array.from(lg.claim_ciphertext),
          });

          const secret = base64urlEncode(lg.claim_key);
          const shareUrl = `${window.location.origin}/#/grant-accept/${grant.id}/${secret}`;

          overlay.remove();
          // Email the share link to the grantee (fire-and-forget)
          post(`/grants/${grant.id}/send-link`, { share_url: shareUrl }).catch(() => {});
          showGrantSuccess(shareUrl, container);
        }
      } else if (isGroupGrant) {
        // Group grant flow
        const groupIdVal = e.target.group_id.value;
        if (!groupIdVal) { msg.innerHTML = '<div class="msg msg-error">Select a group</div>'; btn.disabled = false; overlay.remove(); return; }

        const [group, groupItems] = await Promise.all([
          get(`/groups/${groupIdVal}`),
          get(`/groups/${groupIdVal}/items`),
        ]);

        const progressEl = document.getElementById('wrap-progress');

        if (recipientData) {
          // Wrap group key for recipient
          const wrapped = wrapForGrant(group.wrapped_key, group.nonce, recipientData.public_key);
          const grantWrappedKey = wrapped.grant_wrapped_key;

          // Wrap each item's key for recipient
          const wrappedItemKeys = {};
          for (let i = 0; i < groupItems.length; i++) {
            const item = groupItems[i];
            progressEl.textContent = `Wrapping keys... ${i + 1}/${groupItems.length}`;

            const rawKey = getRawItemKey(item.wrapped_key, item.nonce, item.wrapped_key_format_version);
            const itemWrapped = wrapRawKeyForGrant(rawKey, recipientData.public_key);
            wipe(rawKey);

            const entry = {
              wrapped_key: [...itemWrapped.wrapped_key],
              nonce: [...itemWrapped.nonce],
              ephemeral_pubkey: [...itemWrapped.ephemeral_pubkey],
            };

            // Handle file keys
            try {
              const plaintext = decryptItem(item.encrypted_blob, item.wrapped_key, item.nonce, item.wrapped_key_format_version, item.encrypted_blob_format_version);
              const envelope = JSON.parse(plaintext);
              if (envelope && envelope.file_wrapped_key && envelope.file_nonce) {
                const fileKey = unwrapFileKey(envelope.file_wrapped_key, envelope.file_nonce);
                const fileWrapped = wrapRawKeyForGrant(fileKey, recipientData.public_key);
                wipe(fileKey);
                entry.file_wrapped_key = [...fileWrapped.ephemeral_pubkey, ...fileWrapped.nonce, ...fileWrapped.wrapped_key];
              }
            } catch (_) {}

            wrappedItemKeys[item.id] = entry;
          }

          await post('/grants', {
            group_id: groupIdVal,
            grantee_email: granteeEmail,
            wrapped_key: grantWrappedKey,
            ephemeral_pubkey: wrapped.ephemeral_pubkey,
            policy,
            wrapped_item_keys: wrappedItemKeys,
          });

          overlay.remove();
          showGrantSuccess(null, container);
        } else {
          // Link-secret flow for group
          const lsResult = wrapForLinkSecret(group.wrapped_key, group.nonce);
          const grantWrappedKey = [...lsResult.nonce, ...lsResult.wrapped_key];

          const wrappedItemKeys = {};
          for (let i = 0; i < groupItems.length; i++) {
            const item = groupItems[i];
            progressEl.textContent = `Wrapping keys... ${i + 1}/${groupItems.length}`;

            const rawKey = getRawItemKey(item.wrapped_key, item.nonce, item.wrapped_key_format_version);
            const itemLs = wrapRawKeyForLinkSecret(rawKey, lsResult.link_secret);
            wipe(rawKey);

            const entry = {
              wrapped_key: [...itemLs.wrapped_key],
              nonce: [...itemLs.nonce],
              ephemeral_pubkey: [],
            };

            // Handle file keys
            try {
              const plaintext = decryptItem(item.encrypted_blob, item.wrapped_key, item.nonce, item.wrapped_key_format_version, item.encrypted_blob_format_version);
              const envelope = JSON.parse(plaintext);
              if (envelope && envelope.file_wrapped_key && envelope.file_nonce) {
                const fileKey = unwrapFileKey(envelope.file_wrapped_key, envelope.file_nonce);
                const fileLs = wrapRawKeyForLinkSecret(fileKey, lsResult.link_secret);
                wipe(fileKey);
                entry.file_wrapped_key = [...fileLs.nonce, ...fileLs.wrapped_key];
              }
            } catch (_) {}

            wrappedItemKeys[item.id] = entry;
          }

          const claimKey = crypto.getRandomValues(new Uint8Array(32));
          const claimCiphertext = await encryptClaimSecret(claimKey, lsResult.link_secret);
          const claimTokenHash = await sha256hex(claimKey);

          const grant = await post('/grants', {
            group_id: groupIdVal,
            grantee_email: granteeEmail,
            wrapped_key: grantWrappedKey,
            ephemeral_pubkey: [],
            policy,
            wrapped_item_keys: wrappedItemKeys,
            claim_token_hash: claimTokenHash,
            claim_ciphertext: Array.from(claimCiphertext),
          });

          const secret = base64urlEncode(claimKey);
          const shareUrl = `${window.location.origin}/#/grant-accept/${grant.id}/${secret}`;

          overlay.remove();
          post(`/grants/${grant.id}/send-link`, { share_url: shareUrl }).catch(() => {});
          showGrantSuccess(shareUrl, container);
        }
      } else {
        // Single-item owner flow
        const itemIdVal = e.target.item_id.value;
        if (!itemIdVal) { msg.innerHTML = '<div class="msg msg-error">Select an item</div>'; btn.disabled = false; overlay.remove(); return; }

        const item = await get(`/items/${itemIdVal}`);

        let fileKey = null;
        try {
          const plaintext = decryptItem(item.encrypted_blob, item.wrapped_key, item.nonce, item.wrapped_key_format_version, item.encrypted_blob_format_version);
          const envelope = JSON.parse(plaintext);
          if (envelope && envelope.file_wrapped_key && envelope.file_nonce) {
            fileKey = unwrapFileKey(envelope.file_wrapped_key, envelope.file_nonce);
          }
        } catch (_) {}

        try {
          if (recipientData) {
            const wrapped = wrapForGrant(item.wrapped_key, item.nonce, recipientData.public_key);
            const grantWrappedKey = wrapped.grant_wrapped_key;

            let fileWrappedKeyForGrant = null;
            if (fileKey) {
              const fileWrapped = wrapRawKeyForGrant(fileKey, recipientData.public_key);
              fileWrappedKeyForGrant = [...fileWrapped.ephemeral_pubkey, ...fileWrapped.nonce, ...fileWrapped.wrapped_key];
            }

            await post('/grants', {
              item_id: itemIdVal,
              grantee_email: granteeEmail,
              wrapped_key: grantWrappedKey,
              ephemeral_pubkey: wrapped.ephemeral_pubkey,
              policy,
              file_wrapped_key: fileWrappedKeyForGrant,
            });

            overlay.remove();
            showGrantSuccess(null, container);
          } else {
            const itemKey = getRawItemKey(item.wrapped_key, item.nonce, item.wrapped_key_format_version);
            const lg = prepareLinkGrant(itemKey, fileKey);
            wipe(itemKey);

            const grant = await post('/grants', {
              item_id: itemIdVal,
              grantee_email: granteeEmail,
              wrapped_key: [...lg.nonce, ...lg.wrapped_key],
              ephemeral_pubkey: [],
              policy,
              file_wrapped_key: lg.file_wrapped_key ? [...lg.file_wrapped_key] : null,
              claim_token_hash: lg.claim_token_hash,
              claim_ciphertext: Array.from(lg.claim_ciphertext),
            });

            const secret = base64urlEncode(lg.claim_key);
            const shareUrl = `${window.location.origin}/#/grant-accept/${grant.id}/${secret}`;
            wipe(lg.claim_key, lg.link_secret, lg.wrapped_key);

            overlay.remove();
            post(`/grants/${grant.id}/send-link`, { share_url: shareUrl }).catch(() => {});
            showGrantSuccess(shareUrl, container);
          }
        } finally {
          wipe(fileKey);
        }
      }
    } catch (err) {
      overlay.remove();
      msg.innerHTML = `<div class="msg msg-error">${esc(err.message)}</div>`;
      btn.disabled = false;
    }
  };
}

function showGrantSuccess(shareUrl, container) {
  const el = document.createElement('div');
  el.className = 'share-result';

  let bodyHtml = `
    <div class="check-circle check-pop"><i data-lucide="check" style="width:28px;height:28px"></i></div>
    <h3>Grant Created</h3>`;

  if (shareUrl) {
    bodyHtml += `
      <p>The recipient has been notified by email. You can also share this link directly:</p>
      <div class="copy-link-row">
        <input value="${esc(shareUrl)}" readonly id="grantShareUrl">
        <button class="btn btn-secondary btn-sm" id="copyGrantBtn">Copy</button>
        ${navigator.share ? '<button class="btn btn-primary btn-sm" id="webShareGrantBtn"><i data-lucide="share-2" style="width:14px;height:14px"></i> Share</button>' : ''}
      </div>`;
  } else {
    bodyHtml += '<p>The recipient will receive an email with access instructions.</p>';
  }

  bodyHtml += `<div style="margin-top:16px"><button class="btn btn-ghost" id="grantDoneBtn">Done</button></div>`;

  el.innerHTML = `<div class="share-box">${bodyHtml}</div>`;
  document.body.appendChild(el);
  lucideIcons();

  if (shareUrl) {
    document.getElementById('copyGrantBtn').onclick = () => {
      navigator.clipboard.writeText(shareUrl);
      document.getElementById('copyGrantBtn').textContent = 'Copied!';
    };
    const webShareGrantBtn = document.getElementById('webShareGrantBtn');
    if (webShareGrantBtn) {
      webShareGrantBtn.onclick = () => {
        navigator.share({ title: 'Secure grant on BlindKeep', text: 'You\'ve been granted access to an encrypted item.', url: shareUrl }).catch(() => {});
      };
    }
  }

  document.getElementById('grantDoneBtn').onclick = () => {
    el.remove();
    navigate('#/grants');
  };
}
